import {HttpResponse} from '@angular/common/http';
import {Component, Input, OnInit} from '@angular/core';
import {
    AnalyticAgentApiService,
    AnalyticAgentReportResponse, AnalyticAgentReportType,
    ReferenceBookResponse,
    ReferenceBookType,
} from '@npck/af-frontend-api';
import {downloadFromBlobResponse, rxSpinner} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {combineLatest} from 'rxjs';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../components/full-table/full-table.component';
import {
    ButtonColumn,
    Column,
    ColumnType,
    ELLIPSIS_LARGE_TEXT_MAX_LENGTH,
    Row,
} from '../../../../components/full-table/table/table.component';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {IncidentDetailsFilterHolderService} from '../../../../domain/incident-details-filter-holder.service';
import {
    AnalyticAgentReportFeedbackDialogComponent, AnalyticAgentReportFeedbackDialogData,
} from '../analytic-agent-report-feedback-dialog/analytic-agent-report-feedback-dialog.component';

@Component({
    selector: 'af-analytic-agent-reports-tab',
    templateUrl: './analytic-agent-reports-tab.html',
    styleUrl: './analytic-agent-reports-tab.scss',
    standalone: false,
})
export class AnalyticAgentReportsTab implements OnInit {
    @Input() incidentId: string;
    dataSource: Row[] = [];
    tableEvent: TableEvent;

    constructor(
        private analyticAgentApiService: AnalyticAgentApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private dialogService: DialogService,
        private snackBarService: SnackbarService,
        private filterHolderService: IncidentDetailsFilterHolderService,
    ) {}

    ngOnInit(): void {
        this.loadReports(this.filterHolderService.getState(this.incidentId, 'tabAnalyticAgentReports')?.tableEvent
            ?? this.buildInitialTableEvent());
    }

    loadReports(tableEvent: TableEvent): void {
        const prevEvent = this.filterHolderService.getState(this.incidentId, 'tabAnalyticAgentReports')?.tableEvent;
        this.tableEvent = mergeTableEvent(prevEvent, tableEvent);
        this.filterHolderService.storeTableState(this.incidentId, 'tabAnalyticAgentReports', this.tableEvent);
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
            this.dictionaryHolderService.getReference(ReferenceBookType.AI_ANALYST_RATE),
            this.analyticAgentApiService.getIncidentReports(this.incidentId, {sortOrder: tableEvent?.sortBy?.direction || 'DESC'}),
        ])
            .pipe()
            .subscribe(([organizations, rates, response]) => {
                this.buildDataSource(response, organizations, rates);
            });
    }

    private reloadReports(): void {
        this.loadReports(this.filterHolderService.getState(this.incidentId, 'tabAnalyticAgentReports')?.tableEvent
            ?? this.buildInitialTableEvent());
    }

    private buildDataSource(
        response: AnalyticAgentReportResponse[],
        organizations: ReferenceBookResponse[],
        rates: ReferenceBookResponse[],
    ): void {
        this.dataSource = response.map(item => {
            const createdByOrganization = organizations.find(
                dictionary => dictionary.code === item.createdByOrganization
            )?.name_ru ?? '-';

            let reportFeedbackAction: Column;

            if (item.averageRate) {
                reportFeedbackAction = <ButtonColumn>{
                    type: ColumnType.BUTTON,
                    buttonType: 'text',
                    icon: '',
                    buttonSize: 'small',
                    text: findValueInDictionary(rates, item.averageRate, 'name_ru'),
                    click: () => this.showFeedback(item),
                };
            } else {
                reportFeedbackAction = <ButtonColumn>{
                    type: ColumnType.BUTTON,
                    buttonType: 'primary',
                    icon: '',
                    buttonSize: 'small',
                    text: 'i18n_af_buttonRate',
                    click: () => this.createFeedback(item),
                };
            }

            const downloadButton = <ButtonColumn>{
                type: ColumnType.BUTTON,
                buttonType: 'text',
                icon: 'ic_download',
                buttonSize: 'small',
                text: 'i18n_af_analyticAgentReportTypeShort#' + item.reportType,
                click: () => this.downloadReport(item.id, item.reportType),
            };

            return <Row>{
                created_date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.createdDate,
                },
                createdBy: <Column>{
                    type: ColumnType.TEXT,
                    text: item.createdBy,
                },
                createdByOrganization: <Column>{
                    type: ColumnType.TEXT,
                    text: createdByOrganization,
                    cellStyle: createdByOrganization?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                        ellipsisTextMedium: true,
                    },
                    tooltip: createdByOrganization?.length > ELLIPSIS_LARGE_TEXT_MAX_LENGTH && {
                        text: createdByOrganization,
                    },
                },
                reportFeedbackAction: reportFeedbackAction,
                downloadButton: downloadButton,
            };
        });
    }

    private buildInitialTableEvent(): TableEvent {
        return {
            ...initialTableEvent,
            spinnerId: 'analyticAgentReports',
            filter: {},
            sortBy: {
                columnName: 'createdDate',
                direction: 'desc',
            },
        };
    }

    private showFeedback(report: AnalyticAgentReportResponse): void {
        this.dialogService.openCustomDialogFitContent<AnalyticAgentReportFeedbackDialogComponent, AnalyticAgentReportResponse>(
            AnalyticAgentReportFeedbackDialogComponent,
            <AnalyticAgentReportFeedbackDialogData>{report: report, readonly: true},
            {disableClose: false}
        );
    }

    private createFeedback(report: AnalyticAgentReportResponse): void {
        this.dialogService.openCustomDialogFitContent<AnalyticAgentReportFeedbackDialogComponent, AnalyticAgentReportResponse>(
            AnalyticAgentReportFeedbackDialogComponent,
            <AnalyticAgentReportFeedbackDialogData>{report: report, readonly: false},
            {disableClose: true}
        ).subscribe(result => {
            if (!!result) {
                this.reloadReports();
            }
        });
    }

    private downloadReport(reportId: string, reportType: AnalyticAgentReportType): void {
        this.analyticAgentApiService.downloadIncidentReport(reportId, 'response')
            .pipe(rxSpinner(`downloadFile_${reportId}`))
            .subscribe({
                next: (response: HttpResponse<Blob>) => {
                    reportType === AnalyticAgentReportType.HTML ? this.openFileInNewTab(response) : downloadFromBlobResponse(response);
                },
                error: err => {
                    this.snackBarService.showSnackBar(
                        'i18n_af_snackBarMessageFailedToDownloadAnalyticReport#' + reportType,
                        'error',
                        false
                    );
                }
            });
    }

    private openFileInNewTab(response: HttpResponse<Blob>): void {
        const file = new Blob([response.body], {type: 'text/html'});
        const fileURL = URL.createObjectURL(file);

        // Open the local reference in a blank target tab
        window.open(fileURL, '_blank');
    }
}
